/**
 * InvoiceLines.js
 * Linhas da fatura: uma por procedimento do agendamento, com a origem de cada uma
 * (pacote com "restam N de M sessões" ou avulso) e o rodapé com o total a cobrar.
 * Componente controlado: recebe `lines` e `choices` (de useBillingLines) e devolve
 * a escolha por linha em `onChange(procedureId, source)`.
 *
 * Props:
 *   lines, choices, onChange  — de useBillingLines
 *   locked        — plano sem pacotes: tudo avulso, opção de pacote com cadeado
 *   lockAction    — nó com o atalho de troca de plano (link ou botão), mostrado no aviso
 *   onLockedClick — clique em "Pacote" com o plano bloqueado (ex.: abrir o modal de upgrade)
 *   onSellPackage — quando informado, linha sem saldo ganha o link "Vender pacote"
 *   loading, disabled, error — estado do formulário; `error` aparece no rodapé
 *
 * Usado pelo InvoiceForm (agenda v2) e pelo CreateInvoicePopup.
 */

import React from 'react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { formatPrice } from '../../utils/appointmentUtils';
import { planAvailabilityText, PROFESSIONAL_PLAN_NAME } from '../../../../../components/subscription/planLockTexts';
import { SOURCE_DIRECT, SOURCE_PACKAGE, summarizeLines } from './useBillingLines';
import styles from './InvoiceLines.module.css';

// expiration_date chega como "YYYY-MM-DD"; lendo o texto evitamos o fuso do new Date()
const fmtDay = (str) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(str || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
};

const sessionsLabel = (n) => `${n} ${n === 1 ? 'sessão' : 'sessões'}`;

/** "Laser axila · restam 4 de 10 sessões · vence 12/03/2027" */
const creditText = (credit) => {
  const parts = [credit.package_name || 'Créditos'];
  const remaining = Number(credit.credits_remaining) || 0;
  const total = Number(credit.credits_total) || 0;
  parts.push(total > 0
    ? `restam ${remaining} de ${total} ${remaining === 1 && total === 1 ? 'sessão' : 'sessões'}`
    : `restam ${sessionsLabel(remaining)}`);
  const due = fmtDay(credit.expiration_date);
  if (due) parts.push(`vence ${due}`);
  return parts.join(' · ');
};

export default function InvoiceLines({
  lines = [],
  choices = {},
  onChange,
  locked = false,
  lockAction = null,
  onLockedClick,
  onSellPackage,
  loading = false,
  disabled = false,
  error = '',
}) {
  const summary = summarizeLines(lines, choices);
  const hasAnyCredit = lines.some(line => line.credit);

  if (loading && lines.length === 0) {
    return (
      <div className={styles.loading}>
        <span className={styles.spinner} />
        <span>Carregando procedimentos...</span>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {locked && (
        <div className={styles.lockNote}>
          <LockOutlinedIcon className={styles.lockNoteIcon} />
          <span>
            Pacotes de serviços {planAvailabilityText(PROFESSIONAL_PLAN_NAME, true)}.
            {lockAction && <> {lockAction}</>}
          </span>
        </div>
      )}

      <div className={styles.list}>
        {lines.map(line => {
          const source = choices[line.procedure_id] === SOURCE_PACKAGE ? SOURCE_PACKAGE : SOURCE_DIRECT;
          const usingPackage = source === SOURCE_PACKAGE;
          const hasCredit = !!line.credit;
          return (
            <div key={line.procedure_id ?? line.procedure_name} className={styles.line}>
              <div className={styles.lineMain}>
                <span className={styles.lineName}>{line.procedure_name}</span>
                <span className={usingPackage ? styles.linePriceMuted : styles.linePrice}>
                  {formatPrice(line.price)}
                </span>
              </div>

              <div className={styles.lineOrigin}>
                {hasCredit ? (
                  <div className={styles.pills} role="group" aria-label="Origem da cobrança">
                    <button
                      type="button"
                      className={`${styles.pill} ${usingPackage ? styles.pillActive : ''} ${locked ? styles.pillLocked : ''}`}
                      onClick={() => {
                        if (locked) onLockedClick?.();
                        else onChange?.(line.procedure_id, SOURCE_PACKAGE);
                      }}
                      disabled={disabled}
                      title={locked ? `Pacotes de serviços ${planAvailabilityText(PROFESSIONAL_PLAN_NAME, true)}` : undefined}
                    >
                      {locked && <LockOutlinedIcon className={styles.pillLockIcon} />}
                      Pacote
                    </button>
                    <button
                      type="button"
                      className={`${styles.pill} ${!usingPackage ? styles.pillActive : ''}`}
                      onClick={() => onChange?.(line.procedure_id, SOURCE_DIRECT)}
                      disabled={disabled}
                    >
                      Avulso
                    </button>
                  </div>
                ) : (
                  <span className={styles.chipStatic}>Avulso</span>
                )}

                <span className={styles.lineHint}>
                  {hasCredit
                    ? creditText(line.credit)
                    : (
                      <>
                        Sem pacote com saldo
                        {onSellPackage && !disabled && (
                          <>
                            {' · '}
                            <button type="button" className={styles.linkBtn} onClick={onSellPackage}>
                              Vender pacote
                            </button>
                          </>
                        )}
                      </>
                    )}
                </span>
              </div>
            </div>
          );
        })}
        {lines.length === 0 && (
          <div className={styles.empty}>Este agendamento não tem procedimento para faturar.</div>
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.footerLine}>
          <span className={styles.footerLabel}>Total a cobrar</span>
          <span className={styles.footerTotal}>{formatPrice(summary.directTotal)}</span>
        </div>
        {summary.packageCount > 0 && (
          <div className={styles.footerLine}>
            <span className={styles.footerLabel}>
              {sessionsLabel(summary.packageCount)} de pacote
            </span>
            <span className={styles.footerMuted}>
              {summary.packageCount === 1 ? 'descontada ao faturar' : 'descontadas ao faturar'}
            </span>
          </div>
        )}
        {!locked && !hasAnyCredit && lines.length > 1 && (
          <div className={styles.footerMuted}>Nenhum procedimento com pacote: cobrança avulsa.</div>
        )}
        {error && <div className={styles.error}>{error}</div>}
      </div>
    </div>
  );
}
